<template>
  <div class="card summary-card">
    <div class="card-body">
      <!-- Current -->
      <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
        <div>
          <p class="summary-card__section-label mb-1">Current</p>
          <p class="summary-card__product mb-0">{{ display.productName }}</p>
        </div>
        <StatusPill :state="display.pillState" />
      </div>

      <div class="summary-card__row">
        <span class="summary-card__label">Price</span>
        <span class="summary-card__value">{{ display.priceString }}</span>
      </div>
      <div v-if="display.startedOn" class="summary-card__row">
        <span class="summary-card__label">Started on</span>
        <span class="summary-card__value">{{ display.startedOn }}</span>
      </div>

      <!-- Stripe couldn't collect: tell them why PRO+ stopped and what to do. -->
      <div v-if="display.paymentFailedMessage" class="summary-card__payment-failed">
        <span class="material-icons summary-card__payment-failed-icon">error_outline</span>
        <p class="mb-0 small">{{ display.paymentFailedMessage }}</p>
      </div>

      <!-- Upcoming -->
      <template v-if="display.renewsOn || display.accessEndsOn">
        <hr class="summary-card__divider" />
        <p class="summary-card__section-label mb-2">Upcoming</p>
        <!-- While paused these are two different dates: access stops when the
             period already paid for runs out, billing resumes later. -->
        <div v-if="display.accessEndsOn" class="summary-card__row">
          <span class="summary-card__label">{{ display.accessEndsOnLabel }}</span>
          <span class="summary-card__value">{{ display.accessEndsOn }}</span>
        </div>
        <div v-if="display.renewsOn" class="summary-card__row">
          <span class="summary-card__label">{{ display.renewsOnLabel }}</span>
          <span class="summary-card__value">{{ display.renewsOn }}</span>
        </div>
      </template>

      <!-- This subscription is about to charge for time another entitlement
           already covers. Warned here rather than buried in an email, because
           this is the screen the customer is on when they think to check. -->
      <div v-if="display.renewalNudge" class="summary-card__nudge" role="status">
        <span class="material-icons summary-card__nudge-icon">info</span>
        <span>{{ display.renewalNudge }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import StatusPill from "./StatusPill.vue";
import type { DisplayModel } from "../subscriptionLogic";

defineProps<{ display: DisplayModel }>();
</script>

<style scoped lang="scss">
.summary-card {
  border-radius: 16px;

  &__section-label {
    font-weight: 700;
    font-size: 0.95rem;
  }

  &__product {
    font-weight: 700;
    font-size: 1.05rem;
  }

  &__row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.35rem 0;
  }

  &__label {
    color: var(--text-muted, #b2b2b2);
    font-size: 0.95rem;
  }

  &__value {
    font-weight: 700;
    text-align: right;
  }

  &__divider {
    margin: 1rem 0;
    opacity: 0.15;
  }

  &__nudge {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 1rem;
    padding: 0.75rem;
    border: 1px solid rgba(251, 140, 0, 0.5);
    border-radius: 10px;
    background-color: rgba(251, 140, 0, 0.12);
    font-size: 0.9rem;
    line-height: 1.4;

    &-icon {
      font-size: 1.1rem;
      flex-shrink: 0;
      color: #fb8c00;
    }
  }

  &__payment-failed {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding: 0.6rem 0.75rem;
    border-radius: 10px;
    background-color: rgba(220, 53, 69, 0.12);
  }

  &__payment-failed-icon {
    color: #dc3545;
    font-size: 1.1rem;
  }
}
</style>
